import React, { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { defineMessages } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { hasCustomerToken } from 'src/auth'
import { useClientIdCoinToss } from 'src/hooks'
import { isTouchDevice } from 'src/helpers/browser'
import { QUESTION_SMS_AND_APP_PREFERENCES } from 'src/constants/client/answers'
import { handleAcknowledgeTeaser } from 'src/actions/homeowner'
import { saveClientData } from 'src/actions/client'
import { selectHasClientAnsweredQuestion } from 'src/store/selectors/client'
import { Button, If, Unless } from 'src/Components'

import NotificationPlatformPrompt from './NotificationPlatformPrompt'
import PhoneNumberPrompt from './PhoneNumberPrompt'
import styles from './TeaserWindow.module.scss'

const question = QUESTION_SMS_AND_APP_PREFERENCES

export const TeaserWindow = ({ active }) => {
  const dispatch = useDispatch()
  const intlFormat = useFormatMessage()
  const windowRef = useRef(null)
  const position = useClientIdCoinToss() ? 'right' : 'left'
  const answered = useSelector(selectHasClientAnsweredQuestion(question))
  const [nexted, setNexted] = useState(false)
  const [closed, setClosed] = useState(false)
  const [response, setResponse] = useState({
    email: true,
    app: false,
    text: false,
    mountSize: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    isTouchDevice: isTouchDevice(),
    position: window.innerWidth > 899 ? position : 'center'
  })

  const show = (!answered || nexted) && !closed && !hasCustomerToken()

  const saveAnswer = (answer, notify) => {
    dispatch(handleAcknowledgeTeaser(question, answer, notify))
  }

  const setValueForKey = (key, value) => {
    setResponse({
      ...response,
      [key]: value
    })
  }

  const setNextedOrExit = () => {
    const shouldExit = !response.app && !response.text
    const nextResponse = {
      ...response,
      continued: true
    }
    setResponse(nextResponse)
    setNexted(!shouldExit)
    setClosed(shouldExit)

    saveAnswer(nextResponse, shouldExit)
  }

  const handleClose = () => {
    const exitResponse = {
      ...response,
      bailed: true
    }
    setResponse(exitResponse)
    setClosed(true)

    // Does not alert to preference saving
    // We do of course, but only to know that they don't care about preference saving
    saveAnswer(exitResponse, false)
  }

  const handleSaveMobileAndClose = (mobile, doClientUpdate) => {
    const phoneResponse = {
      ...response,
      phoneInput: mobile,
      updatedClient: doClientUpdate,
      completed: true
    }
    setResponse(phoneResponse)
    setClosed(true)

    if (doClientUpdate) {
      dispatch(saveClientData({ mobile }))
    }
    saveAnswer(phoneResponse, true)
  }

  let subject = intlFormat(subjectMessages.both)
  if (!response.app) {
    subject = intlFormat(subjectMessages.text)
  }
  if (!response.text) {
    subject = intlFormat(subjectMessages.app)
  }

  return (
    <If condition={active}>
      <div
        ref={windowRef}
        className={classnames(
          styles.callout,
          styles[`position-${position}`],
          !show && styles.fadeOut
        )}
      >
        <Button.Close className={styles.closeButton} onClick={handleClose} />
        <Unless condition={nexted}>
          <NotificationPlatformPrompt
            handleChange={setValueForKey}
            handleNext={setNextedOrExit}
          />
        </Unless>
        <If condition={nexted}>
          <PhoneNumberPrompt
            handleSave={handleSaveMobileAndClose}
            copySubject={subject}
          />
        </If>
      </div>
    </If>
  )
}

const subjectMessages = defineMessages({
  both: {
    id: 'Homeowner.teasers.notification.platform.editing.both',
    defaultMessage: 'Texts and our app'
  },
  app: {
    id: 'Homeowner.teasers.notification.platform.editing.app',
    defaultMessage: 'Our app'
  },
  text: {
    id: 'Homeowner.teasers.notification.platform.editing.text',
    defaultMessage: 'Texts'
  }
})

TeaserWindow.propTypes = {
  active: PropTypes.bool
}

export default TeaserWindow
